import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import Icon from '../components/ui/Icons';
import useReveal from '../components/ui/Reveal';
import COMPANY, { ROUTES } from '../config/company';

import heroImage from '../assets/Images/about-group-photo.webp';
import yardImage from '../assets/Images/DivImage1.png';
import truckImage from '../assets/Images/TruckImage_For_general_contact.webp';

const VALUES = [
  ['shield', 'Road-ready, every time', 'A trailer that fails inspection costs you a load. Every unit leaves our yard with a current DOT annual and a documented pre-rental check.'],
  ['bolt', 'Speed over paperwork', 'Carriers sign up once. After that, reserving a trailer is a call or a text, and pickup is hook-up-and-go.'],
  ['dollar', 'Honest pricing', 'Flat monthly rates, no mileage fees, no hidden charges. The number we quote is the number on the invoice.'],
  ['chat', 'A person answers', 'Our team is reachable by phone, email and text during business hours, with a 24/7 line for roadside issues.'],
];

const LOCATIONS = [
  { ...COMPANY.hq, label: 'Business address', note: 'Business contact address. Trailer pickup locations are confirmed separately.' },
];

export default function About() {
  useReveal();
  return (
    <>
      <PageHero
        eyebrow="About Bitnex Freight LLC"
        title="Built by people who have waited at a rental counter."
        lede="Bitnex Freight LLC rents DOT-inspected trailers to carriers and owner-operators who need capacity without the long-term lease. Our business address is 4560 Rosswood Dr, Memphis, TN 38128; trailer pickup locations are confirmed separately."
        image={heroImage}
      />

      <section className="py-20 sm:py-24">
        <div className="shell grid items-center gap-12 lg:grid-cols-2">
          <div className="reveal">
            <div className="eyebrow mb-3">Our vision</div>
            <h2 className="h2">Reliable, high-quality rental trailers with exceptional service.</h2>
            <p className="mt-5 text-lg leading-relaxed text-ink-600">
              We aim to deliver convenience, efficiency and peace of mind every step of the way: a trailer that is
              ready when you arrive, pricing you can plan around, and a support team that picks up the phone.
            </p>
            <p className="mt-4 leading-relaxed text-ink-600">
              Bitnex Freight LLC started with a handful of dry vans and a simple idea: owner-operators should be able to add a
              trailer to their business as easily as they add a load. Today we run dry van, reefer and flatbed
              equipment across the Southeast and along major lanes nationwide.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={ROUTES.trailers} className="btn-dark">
                See the fleet <Icon name="arrowRight" className="h-4 w-4" />
              </Link>
              <Link to={ROUTES.contact} className="btn-outline">
                Contact us
              </Link>
            </div>
          </div>
          <div className="reveal grid grid-cols-2 gap-4" style={{ '--reveal-delay': '120ms' }}>
            <img src={yardImage} alt="Freight yard with trailers" className="h-full w-full rounded-2xl object-cover shadow-card" loading="lazy" />
            <img src={truckImage} alt="Truck with box trailer" className="mt-10 h-full w-full rounded-2xl object-cover shadow-card" loading="lazy" />
          </div>
        </div>
      </section>

      <section className="bg-ink-50 py-20 sm:py-24">
        <div className="shell">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3">What we stand for</div>
            <h2 className="h2">Four promises on every rental.</h2>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {VALUES.map(([icon, title, body], i) => (
              <div key={title} className="card reveal p-7" style={{ '--reveal-delay': `${(i % 2) * 90}ms` }}>
                <span className="mb-5 grid h-12 w-12 place-items-center rounded-lg bg-navy-900 text-gold-300">
                  <Icon name={icon} className="h-5 w-5" />
                </span>
                <h3 className="font-display text-xl font-bold">{title}</h3>
                <p className="mt-2 text-[0.92rem] leading-relaxed text-ink-600">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24">
        <div className="shell">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3">Locations</div>
            <h2 className="h2">Where to find us.</h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {LOCATIONS.map((loc) => (
              <div key={loc.label} className="card reveal flex gap-5 p-7">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-gold-300 text-navy-900">
                  <Icon name="pin" className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-display text-xl font-bold">{loc.label}</h3>
                  <p className="mt-1 text-ink-700">
                    {loc.line1}
                    <br />
                    {loc.line2}
                  </p>
                  <p className="mt-2 text-[0.85rem] text-ink-500">{loc.note}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="card reveal mt-5 grid gap-6 p-7 sm:grid-cols-3">
            <div>
              <div className="text-[0.7rem] font-bold uppercase tracking-wider text-ink-500">Contact</div>
              <a href={COMPANY.phone || COMPANY.email} className="font-semibold text-navy-900 hover:underline">{COMPANY.phone ? COMPANY.phoneDisplay : COMPANY.emailDisplay}</a>
            </div>
            <div>
              <div className="text-[0.7rem] font-bold uppercase tracking-wider text-ink-500">Email</div>
              <a href={COMPANY.email} className="font-semibold text-navy-900 hover:underline">{COMPANY.emailDisplay}</a>
            </div>
            <div>
              <div className="text-[0.7rem] font-bold uppercase tracking-wider text-ink-500">Hours</div>
              <div className="font-semibold text-navy-900">{COMPANY.hours}</div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
